import { useAtomValue, useSetAtom } from 'jotai';
import { IconButton } from '@carbon/react';
import { Close, WarningAltFilled } from '@carbon/icons-react';
import { tcOverlappingUnitsAtom } from 'pages/type-curves/type-curve-filters/atoms';
import { tcDetailsRightPanelAtom } from '../atoms';
import styles from './inventory-guide-modal.module.scss';

export const InventoryGuideModal = () => {
  const overlapping = useAtomValue(tcOverlappingUnitsAtom);
  const setRightPanel = useSetAtom(tcDetailsRightPanelAtom);

  const unitCount = overlapping.length;
  const pairCount = Math.round(
    overlapping.reduce((sum, o) => sum + o.overlappingUnits.length, 0) / 2
  );
  const close = () => setRightPanel({ type: 'dca', open: false });

  return (
    <aside
      className={styles.panel}
      role="region"
      aria-label="Inventory management guide"
    >
      <div className={styles.header}>
        <div className={styles.headerText}>
          <h2 className={styles.title}>Inventory Guide</h2>
          <p className={styles.subtitle}>Step-by-step inventory management</p>
        </div>
        <IconButton
          kind="ghost"
          label="Close guide"
          align="left"
          onClick={close}
        >
          <Close />
        </IconButton>
      </div>

      <div className={styles.body}>
        <div className={styles.callout}>
          <span className={styles.calloutIcon} aria-hidden="true">
            <WarningAltFilled />
          </span>
          <div className={styles.calloutCopy}>
            <p className={styles.calloutLead}>
              This Type Curve has{' '}
              <strong>
                {unitCount} {unitCount === 1 ? 'unit' : 'units'}
              </strong>{' '}
              with major geometry overlap{pairCount === 1 ? '' : 's'} (
              <strong>
                {pairCount} {pairCount === 1 ? 'pair' : 'pairs'}
              </strong>
              ).
            </p>
            <p className={styles.calloutBody}>
              Overlaps only block the <strong>inventory</strong> side of the
              workspace — Generate LTD geometries, per-unit verdicts, and the
              Verify Inventory task all stay locked until every overlap is
              resolved.
            </p>
          </div>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>1. Resolve overlapping units</h3>
          <ol className={styles.steps}>
            <li>
              <span className={styles.stepNum}>1</span>
              <span>
                Click any <strong className={styles.swatch}>red region</strong>{' '}
                on the map — those are the overlapping areas. The unit you click
                becomes the selected unit.
              </span>
            </li>
            <li>
              <span className={styles.stepNum}>2</span>
              <span>
                A <strong>side panel</strong> opens listing every unit that
                conflicts with your selection. Pick which one to{' '}
                <strong>Keep</strong> — the others are dropped from the
                workspace.
              </span>
            </li>
            <li>
              <span className={styles.stepNum}>3</span>
              <span>
                Repeat for every overlap pair. The pulse stops on the map once
                the last pair is cleared. The{' '}
                <strong>Resolve unit overlaps</strong> task takes a verdict
                whenever you are ready, with pairs still open. Promoting needs
                that verdict, correct or incorrect.
              </span>
            </li>
          </ol>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            2. Generate baseline inventory
          </h3>
          <p className={styles.sectionBody}>
            Click <strong>Generate LTD Inventory Geometries</strong> on the gun
            barrel toolbar. This creates LTD laterals for every valid unit
            inside the TC Area in one click — setting the foundation for
            per-unit review.
          </p>
          <div className={styles.hint}>
            This button is disabled while overlaps remain or spacing is below
            200 ft.
          </div>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>3. Scope &amp; refine units</h3>
          <ol className={styles.steps}>
            <li>
              <span className={styles.stepNum}>1</span>
              <span>
                Click a unit on the map or use the <strong>← / → arrows</strong>{' '}
                on the gun barrel to scope to it.
              </span>
            </li>
            <li>
              <span className={styles.stepNum}>2</span>
              <span>
                Drag the{' '}
                <strong className={styles.sectionLineSwatch}>
                  pink section line
                </strong>{' '}
                across the unit to pick a more representative slice.
              </span>
            </li>
            <li>
              <span className={styles.stepNum}>3</span>
              <span>
                Click <strong>Generate LTD Geometries</strong> (scoped) to
                re-generate just that unit at the new section position.
              </span>
            </li>
          </ol>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>4. Remove slots</h3>
          <p className={styles.sectionBody}>
            On the gun barrel, <strong>click any slot square</strong> to toggle
            it off. Toggled-off slots are excluded from the inventory and appear
            dimmed on the chart.
          </p>
          <ol className={styles.steps}>
            <li>
              <span className={styles.stepNum}>1</span>
              <span>Scope to the unit whose slots you want to adjust.</span>
            </li>
            <li>
              <span className={styles.stepNum}>2</span>
              <span>
                Click any <strong>slot square</strong> on the gun barrel to
                toggle it off (dimmed) or back on.
              </span>
            </li>
            <li>
              <span className={styles.stepNum}>3</span>
              <span>
                After toggling, an inline <strong>note input</strong> appears
                anchored to the slot — add a reason for removing it.
              </span>
            </li>
            <li>
              <span className={styles.stepNum}>4</span>
              <span>
                Review the <strong>Slots Table</strong> below the gun barrel to
                confirm which slots are active and which are excluded.
              </span>
            </li>
          </ol>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>5. Adjust spacing</h3>
          <p className={styles.sectionBody}>
            The <strong>Target spacing</strong> control in the left settings
            panel determines the distance between generated slots. Changing
            spacing regenerates the inventory layout for the workspace.
          </p>
          <ol className={styles.steps}>
            <li>
              <span className={styles.stepNum}>1</span>
              <span>
                Open the <strong>left settings panel</strong> (toggle via the
                panel icon in the header).
              </span>
            </li>
            <li>
              <span className={styles.stepNum}>2</span>
              <span>
                Locate the <strong>Target spacing (ft)</strong> input in the
                settings.
              </span>
            </li>
            <li>
              <span className={styles.stepNum}>3</span>
              <span>
                Set a new value (minimum 200 ft). The gun barrel and map will
                update to reflect the new slot distribution.
              </span>
            </li>
            <li>
              <span className={styles.stepNum}>4</span>
              <span>
                Re-run <strong>Generate LTD Inventory Geometries</strong> to
                rebuild the baseline with the updated spacing.
              </span>
            </li>
          </ol>
          <div className={styles.hint}>
            Spacing changes are logged as workspace actions and appear in the
            Tasks panel as verification items.
          </div>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            6. Verify units &amp; mark verdicts
          </h3>
          <ol className={styles.steps}>
            <li>
              <span className={styles.stepNum}>1</span>
              <span>
                With a unit scoped, click{' '}
                <strong className={styles.correctBadge}>Mark Correct</strong> or{' '}
                <strong className={styles.incorrectBadge}>
                  Mark Incorrect
                </strong>{' '}
                on the gun barrel toolbar.
              </span>
            </li>
            <li>
              <span className={styles.stepNum}>2</span>
              <span>
                Repeat for every active unit. The{' '}
                <strong>Verify Inventory</strong> task in the Tasks panel tracks
                progress.
              </span>
            </li>
            <li>
              <span className={styles.stepNum}>3</span>
              <span>
                Once every unit has a verdict, the Verify Inventory task unlocks
                and can be marked{' '}
                <strong className={styles.correctBadge}>Correct</strong>.
              </span>
            </li>
          </ol>
        </div>

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>7. Promote the workspace</h3>
          <p className={styles.sectionBody}>
            When all tasks are complete, click <strong>Promote</strong> in the
            header. The workspace captures a snapshot of removed units,
            generated LTDs, unit verdicts, toggled slots, and per-slot notes —
            and persists them back onto the Type Curve for everyone to see.
          </p>
        </div>
      </div>
    </aside>
  );
};

InventoryGuideModal.displayName = 'InventoryGuideModal';
